import React, { useContext } from "react";
import _ from "lodash";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Box,
} from "@mui/material";
import FileView from "../../../../components/VectorIcons/FileView";
import EditIcon from "../../../../components/VectorIcons/EditIcon";
import DownloadIcon from "../../../../components/VectorIcons/DownloadIcon";
import AppContext from "../../../../context/appContext";
import UserContext from "../../../../context/userContext";
import ReleaseTextIcon from "../../../../components/VectorIcons/ReleaseTextIcon";
import ReleaseTextIconMarked from "../../../../components/VectorIcons/ReleaseTextIconMarked";
import DeleteIcons from "../BlockList/DeleteIcons/DeleteIcons";
import useDownloadAttachment from "../../../../hooks/useDownloadAttachment";
import ProjectSheet from "../../../../context/sheetContext";
import AttachmentsContext from "../../../../context/attachmentsContext";
import { VIEWABLE_FILE_TYPES } from "../../../../shared/constants/store";
import CustomTooltip from "../../../../components/CustomTooltip/CustomTooltip";
import { getFileExtensionFromFileName } from "../../../../shared/utils/getFileExtensionFromFileName";
import { ActionsMenu } from "../../../../components/ActionsMenu/ActionsMenu";
import DeleteTooltip from "../DeleteTooltip/DeleteTooltip";
import { getLatestReleaseComment } from "../../../AttachmentsScheduler/utils";
import { getReleaseDestinationLabel } from "../../../../utils";

const FONT_BOLD = '"Globotipo Text Bold", sans-serif';
const FONT_LIGHT = '"Globotipo Text Light", sans-serif';
const CYAN_50 = "#0098cd";

const ROW_VARIANT = {
  MAIN: "mainItem",
  COLLAPSED: "colapsedItem",
};

const checkboxSx = {
  color: "#43444D",
  padding: 0,
  "&.Mui-checked": {
    color: "#43444D",
  },
};

// Column widths shared between the header row and the body rows so the
// native <table> column-sizing algorithm keeps them aligned automatically.
const columnSx = {
  select: { width: 72, pl: "24px", pr: "24px" },
  type: { width: 230, pl: 0 },
  version: { width: 60, textAlign: "center" },
  name: { width: 340 },
  receivedAt: { width: 120 },
  releasedAt: { width: 148 },
  actions: (narrow) => ({
    width: narrow ? 100 : 130,
    textAlign: "right",
    pr: 3,
  }),
};

const headerCellSx = {
  fontFamily: FONT_BOLD,
  color: "#7f808e",
  textTransform: "uppercase",
  fontSize: 12,
  fontWeight: "bold",
  borderBottom: "1px solid rgb(192, 195, 248)",
  whiteSpace: "nowrap",
};

// Pure helper hoisted out of the component so it isn't recreated on every
// render and doesn't shadow the row's `item` prop.
const getLatestReleaseHistoryEntry = (history) =>
  history?.reduce(
    (latest, entry) =>
      !latest || new Date(entry.actionDate) > new Date(latest.actionDate)
        ? entry
        : latest,
    null
  );

const ProjectSheetAttachmentsTableRow = React.memo((props) => {
  const {
    item,
    list,
    condition,
    toggleGroupList,
    havePermission,
    handleEditFile,
    variant,
    handleConfirmationDeleteFile,
    handleOpenReleaseTexts,
    returnColor,
    type,
    isExternal,
    haveExternalPermission,
    downloadContext,
    isSelectionMode = false,
    isSelected = false,
    toggleSelectFile = () => {},
    canDelete,
    getDeleteUnavailableMessage,
    canDownload,
    canReleaseChannel,
    canUnReleaseChannel,
    getReleaseUnavailableMessage,
    getUnReleaseUnavailableMessage,
  } = props;
  const appContext = useContext(AppContext);
  const projectSheet = useContext(ProjectSheet);
  const userContext = useContext(UserContext);
  const { setFileToView } = useContext(AttachmentsContext);
  const { downloadAttachment } = useDownloadAttachment();

  const isMainAndHasVersions =
    variant === ROW_VARIANT.MAIN && !!list && list.length > 1;

  const isDeletable = canDelete?.(item.id);
  const deleteUnavailableMessage = getDeleteUnavailableMessage?.(item.id);
  const canDownloadThis = canDownload?.(item.id);

  const fileToDownload = {
    fileData: item,
    contentId: projectSheet.list.result.id,
    origin: downloadContext,
    mode: 0,
  };

  const fileToVisualize = {
    ...fileToDownload,
    origin: `visualizador-${downloadContext}`,
    mode: 1,
    autoSave: false,
  };

  const fileExtension = getFileExtensionFromFileName(item?.fileName);
  const canViewFile =
    VIEWABLE_FILE_TYPES.includes(fileExtension) && item?.fileName !== null;

  // Some callers (e.g. LiteraryWorks/Varieties "editAttachment" modals) don't wire
  // canDownload at all; only enforce the download permission where it's actually available.
  const isDownloadPermissionWired = typeof canDownload === "function";
  const isVisualizeDisabled =
    !canViewFile || (isDownloadPermissionWired && !canDownloadThis);

  const fileType =
    typeof item?.type === "string" ? item.type : item?.type?.name;

  const isActionsNarrow = isExternal || haveExternalPermission;

  const isReleasedToChannel = Boolean(
    item?.releaseManagement?.channel
  );

  const isReleasedToProduction = Boolean(
    item?.releaseManagement?.production
  );

  const isReleasedToCultureHub = Boolean(
    item?.releaseManagement?.cultureHub
  );

  const isReleased =
    isReleasedToChannel || isReleasedToProduction || isReleasedToCultureHub;

  const releaseDestination = getReleaseDestinationLabel({
    channel: isReleasedToChannel,
    production: isReleasedToProduction,
    cultureHub: isReleasedToCultureHub,
  });

  const releaseTooltip = releaseDestination
    ? `Liberado para ${releaseDestination}`
    : "";

  const releaseDate = isReleased
    ? getLatestReleaseHistoryEntry(
        item?.releaseManagement?.history
      )?.actionDate
    : null;

  const hasReleasePermission = userContext.hasPermission({
    required: ["AttachmentRelease"],
  });

  const canReleaseThis = isReleased
    ? canUnReleaseChannel?.(item.id)
    : canReleaseChannel?.(item.id);

  const releaseUnavailableMessage = isReleased
    ? getUnReleaseUnavailableMessage?.(item.id)
    : getReleaseUnavailableMessage?.(item.id);

  return (
    <TableRow
      sx={{
        backgroundColor: variant === ROW_VARIANT.MAIN ? "#fff" : "#eaecf7",
        "& .MuiTableCell-root": {
          borderBottom: "1px solid #d4d5e2",
          fontFamily: "inherit",
          color: "rgb(67, 68, 78)",
          fontSize: 14,
        },
      }}
    >
      <TableCell sx={columnSx.select}>
        {isSelectionMode && (
          <Checkbox
            checked={isSelected}
            onChange={(ev) => toggleSelectFile(item, ev)}
            sx={checkboxSx}
          />
        )}
      </TableCell>

      <TableCell
        sx={{ ...columnSx.type, fontWeight: "bold", color: "#1e1f24" }}
      >
        {fileType}
        {fileType === condition && item.chapterNumber}
        {fileType === condition && (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              border: "1px solid #7f808e",
              color: "#7f808e",
              padding: "3px 6px 0 6px",
              borderRadius: "2px",
              fontFamily: FONT_LIGHT,
              textTransform: "uppercase",
              marginLeft: "8px",
              fontSize: 11,
              letterSpacing: "0.1em",
              marginBottom: "2px",
            }}
          >
            Bloco {item.blockNumber}
          </Box>
        )}
      </TableCell>

      {!haveExternalPermission && (
        <TableCell
          sx={{
            ...columnSx.version,
            cursor: isMainAndHasVersions ? "pointer" : "default",
            ...(isMainAndHasVersions && {
              color: CYAN_50,
              fontWeight: "bold",
            }),
          }}
          onClick={() => isMainAndHasVersions && toggleGroupList(item.id)}
        >
          {item?.version} ª
          {isMainAndHasVersions && (
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: 0,
                height: 0,
                borderStyle: "solid",
                borderWidth: "5px 5px 0 5px",
                borderColor: "#999be0 transparent transparent transparent",
                marginLeft: "5px",
                marginBottom: "2px",
              }}
            />
          )}
        </TableCell>
      )}

      <TableCell sx={{ ...columnSx.name, wordBreak: "break-all" }}>
        {item?.fileName}
      </TableCell>

      <TableCell sx={columnSx.receivedAt}>
        {appContext.convertDatePtBr(
          item?.deliveryControl?.completionDates?.received
        )}
      </TableCell>

      {!haveExternalPermission && (
        <TableCell sx={columnSx.releasedAt}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {releaseDate ? appContext.convertDatePtBr(releaseDate) : "-"}
            {!!isReleased && (
              <CustomTooltip
                dataTip={releaseTooltip}
                tipLabel={
                  <Box sx={{ display: "flex" }}>
                    <ReleaseTextIconMarked />
                  </Box>
                }
                theme="light"
                position="bottom"
              />
            )}
          </Box>
        </TableCell>
      )}

      <TableCell sx={columnSx.actions(isActionsNarrow)}>
        <ActionsMenu
          menuOptions={[
            {
              action: () => setFileToView(fileToVisualize),
              label: "Visualizar",
              disabled: isVisualizeDisabled,
              tooltip: !canViewFile
                ? "Formato de arquivo não suportado para visualização"
                : null,
              icon: <FileView color={isVisualizeDisabled ? "#CCC" : "#0098CD"} />,
            },
            havePermission && {
              action: () => handleEditFile(item),
              label: "Editar",
              icon: <EditIcon />,
            },
            {
              action: () => downloadAttachment(fileToDownload),
              label: "Baixar",
              disabled: !canDownloadThis,
              icon: <DownloadIcon color={!canDownloadThis ? "#CCC" : "#8C8EF5"} />,
            },
            type !== "literaryworks" &&
              !isExternal && {
                action: () =>
                  handleOpenReleaseTexts(
                    {
                      ...item,
                      observation: getLatestReleaseComment(
                        item?.releaseManagement
                      ),
                    },
                    list
                  ),
                disabled: !hasReleasePermission || !canReleaseThis,
                tooltip: releaseUnavailableMessage ? (
                  <DeleteTooltip message={releaseUnavailableMessage} />
                ) : null,
                label: isReleased
                  ? "Liberado"
                  : "Liberação",
                icon: isReleased ? (
                  <ReleaseTextIconMarked />
                ) : (
                  <ReleaseTextIcon />
                ),
              },
            havePermission && {
              action: () => handleConfirmationDeleteFile(item),
              label: "Remover",
              disabled: !isDeletable,
              tooltip: deleteUnavailableMessage ? (
                <DeleteTooltip message={deleteUnavailableMessage} />
              ) : null,
              icon: <DeleteIcons returnColor={returnColor} />,
            },
          ]}
          disabled={isSelectionMode}
        />
      </TableCell>
    </TableRow>
  );
});

ProjectSheetAttachmentsTableRow.displayName = "ProjectSheetAttachmentsTableRow";

const ProjectSheetAttachmentsTable = (props) => {
  const {
    data,
    haveExternalPermission,
    isExternal,
    condition,
    type,
    havePermission,
    isSelectionMode,
    isFileSelected,
    toggleSelectFile,
    areAllSelected,
    handleToggleSelectAll,
    loading,
    handleEditFile,
    handleConfirmationDeleteFile,
    handleOpenReleaseTexts,
    returnColor,
    downloadContext,
    canDelete,
    getDeleteUnavailableMessage,
    canReleaseChannel,
    canUnReleaseChannel,
    getReleaseUnavailableMessage,
    getUnReleaseUnavailableMessage,
    canDownload,
    colapsedItems,
    toggleGroupList,
  } = props;

  const isActionsNarrow = isExternal || haveExternalPermission;

  return (
    <TableContainer
      sx={{
        overflowX: "auto",
        "& .MuiTableCell-root, & .MuiTypography-root": {
          fontFamily: "inherit",
        },
      }}
    >
      <Table sx={{ minWidth: haveExternalPermission ? 640 : 900 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, ...columnSx.select }}>
              {isSelectionMode && (
                <Checkbox
                  checked={areAllSelected()}
                  onChange={handleToggleSelectAll}
                  disabled={loading}
                  sx={checkboxSx}
                />
              )}
            </TableCell>
            <TableCell sx={{ ...headerCellSx, ...columnSx.type }}>
              Tipo
            </TableCell>
            {!haveExternalPermission && (
              <TableCell sx={{ ...headerCellSx, ...columnSx.version }}>
                Versão
              </TableCell>
            )}
            <TableCell sx={{ ...headerCellSx, ...columnSx.name }}>
              Arquivo
            </TableCell>
            <TableCell sx={{ ...headerCellSx, ...columnSx.receivedAt }}>
              Recebido em
            </TableCell>
            {!haveExternalPermission && (
              <TableCell sx={{ ...headerCellSx, ...columnSx.releasedAt }}>
                Última liberação
              </TableCell>
            )}
            <TableCell
              sx={{
                ...headerCellSx,
                ...columnSx.actions(isActionsNarrow),
              }}
            />
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((group, groupIndex) => {
            const item = haveExternalPermission
              ? group
              : _.maxBy(group, (i) => i.version ?? -Infinity);

            return (
              <React.Fragment key={item?.id ?? item?.fileId ?? groupIndex}>
                <ProjectSheetAttachmentsTableRow
                  item={item}
                  list={group}
                  condition={condition}
                  toggleGroupList={toggleGroupList}
                  havePermission={havePermission}
                  handleEditFile={handleEditFile}
                  handleConfirmationDeleteFile={handleConfirmationDeleteFile}
                  handleOpenReleaseTexts={handleOpenReleaseTexts}
                  returnColor={returnColor}
                  variant={ROW_VARIANT.MAIN}
                  type={type}
                  isExternal={isExternal}
                  haveExternalPermission={haveExternalPermission}
                  downloadContext={downloadContext}
                  isSelectionMode={isSelectionMode}
                  isSelected={isFileSelected(item.fileId)}
                  toggleSelectFile={toggleSelectFile}
                  canDelete={canDelete}
                  getDeleteUnavailableMessage={getDeleteUnavailableMessage}
                  canReleaseChannel={canReleaseChannel}
                  canUnReleaseChannel={canUnReleaseChannel}
                  getReleaseUnavailableMessage={getReleaseUnavailableMessage}
                  getUnReleaseUnavailableMessage={
                    getUnReleaseUnavailableMessage
                  }
                  canDownload={canDownload}
                />

                {!haveExternalPermission &&
                  colapsedItems.includes(item.id) &&
                  _.orderBy(group, ["version"], ["desc"])
                    .slice(1)
                    .map((collapsedItem, itemIndex) => (
                      <ProjectSheetAttachmentsTableRow
                        key={
                          collapsedItem?.id ??
                          collapsedItem?.fileId ??
                          itemIndex
                        }
                        item={collapsedItem}
                        list={group}
                        condition={condition}
                        toggleGroupList={toggleGroupList}
                        havePermission={havePermission}
                        handleEditFile={handleEditFile}
                        handleConfirmationDeleteFile={
                          handleConfirmationDeleteFile
                        }
                        handleOpenReleaseTexts={handleOpenReleaseTexts}
                        returnColor={returnColor}
                        variant={ROW_VARIANT.COLLAPSED}
                        type={type}
                        isExternal={isExternal}
                        haveExternalPermission={haveExternalPermission}
                        downloadContext={downloadContext}
                        isSelectionMode={isSelectionMode}
                        isSelected={isFileSelected(collapsedItem.fileId)}
                        toggleSelectFile={toggleSelectFile}
                        canDelete={canDelete}
                        getDeleteUnavailableMessage={
                          getDeleteUnavailableMessage
                        }
                        canReleaseChannel={canReleaseChannel}
                        canUnReleaseChannel={canUnReleaseChannel}
                        getReleaseUnavailableMessage={
                          getReleaseUnavailableMessage
                        }
                        getUnReleaseUnavailableMessage={
                          getUnReleaseUnavailableMessage
                        }
                        canDownload={canDownload}
                      />
                    ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProjectSheetAttachmentsTable;
